import { useEffect, useState } from 'react'
import { config } from '../config'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const DISMISS_KEY = 'tcpss.installPromptDismissed'

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari bruger sin egen property i stedet for display-mode.
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent)
}

/**
 * Diskret installationsvejledning (kravspec afsnit 52).
 *
 * Android/Windows: browserens eget installationstilbud bruges via beforeinstallprompt.
 * iOS: der findes ingen API - i stedet vises den korte Del -> Foej til hjemmeskaerm-vejledning.
 * Lukker brugeren banneret, vises det ikke igen (gemmes i localStorage).
 */
export function InstallBanner() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIosHint, setShowIosHint] = useState(false)

  useEffect(() => {
    if (isStandalone() || localStorage.getItem(DISMISS_KEY) === '1') {
      return
    }

    function onBeforeInstallPrompt(event: Event) {
      event.preventDefault()
      setPromptEvent(event as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)

    if (isIos()) {
      setShowIosHint(true)
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
  }, [])

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, '1')
    setPromptEvent(null)
    setShowIosHint(false)
  }

  async function install() {
    if (!promptEvent) return
    await promptEvent.prompt()
    await promptEvent.userChoice
    dismiss()
  }

  if (!promptEvent && !showIosHint) {
    return null
  }

  return (
    <div className="banner banner--install">
      <div style={{ marginRight: 'auto' }}>
        <strong>Installér {config.appName} på din enhed</strong>
        <div className="small" style={{ color: '#94A3B8' }}>
          {promptEvent
            ? 'Få appen på skrivebordet eller hjemmeskærmen med eget ikon.'
            : 'Tryk på Del-knappen i Safari og vælg "Føj til hjemmeskærm".'}
        </div>
      </div>
      {promptEvent && (
        <button className="btn btn--sm btn--primary" onClick={install}>
          Installér
        </button>
      )}
      <button className="banner__close" aria-label="Luk" onClick={dismiss}>
        ×
      </button>
    </div>
  )
}
